import { useEffect, useState } from "react";
import { api, formatDateTime } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { Plus, Mail as MailIcon, Send, Inbox as InboxIcon } from "lucide-react";
import { toast } from "sonner";

export default function Emails() {
  const [emails, setEmails] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ to_email: "", subject: "", body: "" });

  const load = () => api.get("/emails").then(r => setEmails(r.data));
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!form.to_email || !form.subject) return toast.error("Recipient and subject required");
    await api.post("/emails", { ...form, direction: "outbound", status: "sent", from_email: "hello@figuraid.com" });
    toast.success("Email queued (MOCKED)");
    setOpen(false);
    setForm({ to_email: "", subject: "", body: "" });
    load();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Email"
        subtitle="Track and send emails linked to contacts"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="compose-email-btn">
                <Plus className="w-4 h-4 mr-1" /> Compose
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New email</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>To</Label><Input type="email" value={form.to_email} onChange={e => setForm({...form, to_email: e.target.value})} data-testid="email-to-input" /></div>
                <div><Label>Subject</Label><Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} data-testid="email-subject-input" /></div>
                <div><Label>Body</Label><Textarea rows={6} value={form.body} onChange={e => setForm({...form, body: e.target.value})} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={send} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="send-email-btn">
                  <Send className="w-4 h-4 mr-1" /> Send
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="text-xs bg-amber-50 text-amber-800 border border-amber-200 px-3 py-2 rounded-md">
        <strong>Note:</strong> Actual email delivery is <strong>MOCKED</strong>. Connect Resend/SendGrid later; the storage/log flow is fully working.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="table-wrap lg:col-span-1">
          {emails.map(e => (
            <button key={e.id} onClick={() => setActive(e)}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${active?.id === e.id ? "bg-brand-aqua-light" : ""}`}
                    data-testid={`email-item-${e.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-slate-800 truncate">
                  {e.direction === "inbound" ? e.from_email : e.to_email}
                </div>
                <Chip variant={e.direction === "inbound" ? "info" : "default"}>
                  {e.direction === "inbound" ? <InboxIcon className="w-3 h-3 mr-0.5" /> : <Send className="w-3 h-3 mr-0.5" />}
                  {e.direction}
                </Chip>
              </div>
              <div className="text-sm text-slate-700 truncate mt-0.5">{e.subject}</div>
              <div className="text-xs text-slate-400 truncate">{e.body?.slice(0, 80)}</div>
              <div className="text-[11px] text-slate-400 mt-1">{formatDateTime(e.created_at)}</div>
            </button>
          ))}
          {emails.length === 0 && <div className="py-12 text-center text-slate-500 text-sm">No emails yet.</div>}
        </div>

        <div className="lg:col-span-2 kpi-card min-h-[400px]">
          {active ? (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <MailIcon className="w-4 h-4 text-slate-400" />
                <div className="font-heading font-semibold text-lg text-slate-900">{active.subject}</div>
              </div>
              <div className="text-xs text-slate-500 mb-4">
                From <strong>{active.from_email}</strong> to <strong>{active.to_email}</strong> · {formatDateTime(active.created_at)}
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{active.body}</div>
            </div>
          ) : (
            <div className="text-slate-400 text-sm text-center py-12">Select an email to preview</div>
          )}
        </div>
      </div>
    </div>
  );
}
